import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Alert, Button } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import RangePickerComponent from "../../components/common/datePickerRange";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { getSociairProfilesApi } from "../../services/socialProfiles.service";
import { exportExcelReport } from "../../utils/exportToExcel";
import { storeDateRange } from "../../store/slices/dateRange.slice";
import MainCards from "./sections/MainCards";
import MessageAndUserTrendChart from "./sections/MessageAndUserTrendChart";
import HumanInterventionTrend from "./sections/HumanInterventionTrend";
import ConversationTopics from "./sections/ConversationTopics";
import UnifiedReportSections from "./sections/UnifiedReportSections";

const toNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const normalizeReportPayload = (payload) => {
  if (!payload || typeof payload !== "object") return {};

  if (payload?.data && (payload?.status || payload?.message)) {
    return payload.data || {};
  }

  return payload;
};

const buildReportTables = (reportData, channelMsgCount, channelMsgCountByRawId) => {
  const generalMetrics = reportData?.generalMetrics || {};
  const salesTeamMetrics = reportData?.salesTeamMetrics || {};
  const detailedActions = reportData?.detailedActions || {};
  const aiPerformance = reportData?.aiPerformance || {};

  const pageWise = Array.isArray(generalMetrics?.pageWise) ? generalMetrics.pageWise : [];
  const packageWise = Array.isArray(generalMetrics?.packageWise) ? generalMetrics.packageWise : [];
  const salesCategories = Array.isArray(salesTeamMetrics?.categories)
    ? salesTeamMetrics.categories
    : [];
  const paymentDetails = Array.isArray(detailedActions?.paymentReceived?.details)
    ? detailedActions.paymentReceived.details
    : [];

  const tables = [];

  tables.push({
    title: "General Totals",
    headers: ["Metric", "Value"],
    rows: [
      ["Received in Sociair", toNumber(generalMetrics?.totals?.receivedInSociair)],
      ["Received in Eco", toNumber(generalMetrics?.totals?.receivedInEco)],
      ["Replied by AI", toNumber(generalMetrics?.totals?.repliedByAi)],
    ],
  });

  const pageWiseByRawIdRows = (() => {
    if (!Array.isArray(channelMsgCountByRawId) || channelMsgCountByRawId.length === 0) return [];

    const grouped = {};
    channelMsgCountByRawId.forEach((item) => {
      const channelType = item?.profile_type || "Not Mentioned";
      const profileName = item?.profile_name || "Not Mentioned";
      const key = `${channelType}__${profileName}`;

      if (!grouped[key]) {
        grouped[key] = {
          channelType,
          profileName,
          totalMessages: 0,
        };
      }

      grouped[key].totalMessages += toNumber(item?.message_count);
    });

    return Object.values(grouped).sort((a, b) => b.totalMessages - a.totalMessages);
  })();

  if (pageWiseByRawIdRows.length) {
    tables.push({
      title: "Page-wise Breakdown",
      headers: ["Channel", "Platform/Page", "Total Messages"],
      rows: pageWiseByRawIdRows.map((item) => [
        item.channelType,
        item.profileName,
        item.totalMessages,
      ]),
    });
  } else if (pageWise.length) {
    tables.push({
      title: "Page-wise Breakdown",
      headers: ["Page", "Sociair", "Eko", "AI Replied"],
      rows: pageWise.map((item) => [
        item.pageName || item.pageId || "-",
        toNumber(item.receivedInSociair),
        toNumber(item.receivedInEco),
        toNumber(item.repliedByAi),
      ]),
    });
  }

  if (packageWise.length) {
    tables.push({
      title: "Package-wise Breakdown",
      headers: ["Package", "Sociair", "Eko", "AI Replied", "Conversations"],
      rows: packageWise.map((item) => [
        item.packageName || item.packageId || "-",
        toNumber(item.receivedInSociair),
        toNumber(item.receivedInEco),
        toNumber(item.repliedByAi),
        toNumber(item.totalConversations),
      ]),
    });
  }

  const channelRows = Array.isArray(channelMsgCount) && channelMsgCount.length > 0
    ? channelMsgCount.map((item) => [
        item?._id || item?.channel || item?.name || "-",
        toNumber(item?.count),
      ])
    : Object.entries(generalMetrics?.channelWiseMessages || {}).map(
        ([channel, count]) => [channel, toNumber(count)]
      );

  if (channelRows.length) {
    tables.push({
      title: "Channel-wise Messages",
      headers: ["Channel", "Count"],
      rows: channelRows,
    });
  }

  if (salesCategories.length) {
    tables.push({
      title: "Sales Team Metrics",
      headers: ["Category", "Total Message", "Conversation", "Received", "Sent"],
      rows: salesCategories.map((item) => [
        item.categoryName || "-",
        toNumber(item.totalMessage),
        toNumber(item.conversation),
        toNumber(item.received),
        toNumber(item.sent),
      ]),
    });
  }

  tables.push({
    title: "Detailed Actions Summary",
    headers: ["Action", "Count"],
    rows: [
      ["Payment Received", toNumber(detailedActions?.paymentReceived?.count)],
      ["QR Sent", toNumber(detailedActions?.qrSent)],
      ["Price Sent", toNumber(detailedActions?.priceSent)],
      ["Ad Click", toNumber(detailedActions?.adClick)],
    ],
  });

  if (paymentDetails.length) {
    tables.push({
      title: "Payment Received Details",
      headers: ["Transaction ID", "Amount", "Timestamp"],
      rows: paymentDetails.map((item) => [
        item.transactionId || "-",
        item.amount ?? "-",
        item.timestamp || "-",
      ]),
    });
  }

  tables.push({
    title: "AI Performance",
    headers: ["Metric", "Value"],
    rows: [
      ["Total Messages in Sociair", toNumber(aiPerformance?.totalMessagesInSociair)],
      ["AI Received Total Messages", toNumber(aiPerformance?.aiReceivedTotalMessages)],
      ["AI Replied Total Messages", toNumber(aiPerformance?.aiRepliedTotalMessages)],
      ["% Handled by AI", toNumber(aiPerformance?.percentageHandledByAi)],
      ["Avg Human Reply in a Day", toNumber(aiPerformance?.avgHumanReplyInDay)],
      ["Human Equivalent Workload", toNumber(aiPerformance?.humanEquivalentWorkload)],
      ["Response Latency Avg Seconds", toNumber(aiPerformance?.responseLatencyAvgSeconds)],
      ["Cost of AI Message", toNumber(aiPerformance?.costOfAiMessage)],
    ],
  });

  const aiChannelRows = Object.entries(aiPerformance?.channelWiseAiMessageCount || {}).map(
    ([channel, count]) => [channel, toNumber(count)]
  );

  if (aiChannelRows.length) {
    tables.push({
      title: "Channel-wise AI Message Count",
      headers: ["Channel", "Count"],
      rows: aiChannelRows,
    });
  }

  return tables;
};

const DashboardComponent = ({ data, fetchCompleted }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dateRangeAntdValue, setDateRangeAntdValue] = useState([
    dayjs().subtract(7, "day"),
    dayjs(),
  ]);
  const [socialMediaProfiles, setSocialMediaProfiles] = useState([]);
  const [reportDownloadLoading, setReportDownloadLoading] = useState(false);
  const [downloadRequested, setDownloadRequested] = useState(false);
  const hasHydratedUrlDatesRef = useRef(false);
  const isHydratingUrlDatesRef = useRef(false);

  const { dateRangeValue } = useAppSelector((state) => state.dateRange);
  const urlStartDate = searchParams.get("start_date");
  const urlEndDate = searchParams.get("end_date");
  const reportData = useMemo(
    () => normalizeReportPayload(data?.unifiedReport || data),
    [data]
  );
  const tables = useMemo(
    () => buildReportTables(reportData, data?.channelMsgCount, data?.channelMsgCountByRawId),
    [reportData, data?.channelMsgCount, data?.channelMsgCountByRawId]
  );
  const fallbackTotalChats = useMemo(() => {
    const totalFromGeneral =
      toNumber(reportData?.generalMetrics?.totals?.receivedInSociair) +
      toNumber(reportData?.generalMetrics?.totals?.receivedInEco);
    return totalFromGeneral;
  }, [reportData]);

  const legacyCardData = useMemo(
    () => ({
      ctaCount: data?.ctaCount ||
        reportData?.ctaCount || {
          data: [],
          total_chats: fallbackTotalChats,
          average_human_Interventions: 0,
        },
      creditBalance: data?.creditBalance || reportData?.creditBalance || {},
      messageCount: data?.messageCount || reportData?.messageCount || [],
    }),
    [data, reportData, fallbackTotalChats]
  );

  useEffect(() => {
    dispatch(
      getSociairProfilesApi({
        params: {},
        finalCallback: () => {},
        successCallback: (response) => {
          setSocialMediaProfiles(response?.profiles || []);
        },
        failureCallback: () => {
          setSocialMediaProfiles([]);
        },
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (hasHydratedUrlDatesRef.current) return;

    hasHydratedUrlDatesRef.current = true;

    const hasValidUrlDates =
      urlStartDate !== null &&
      urlEndDate !== null &&
      urlStartDate !== "" &&
      urlEndDate !== "";

    if (!hasValidUrlDates) return;

    if (
      dateRangeValue?.start_date !== urlStartDate ||
      dateRangeValue?.end_date !== urlEndDate
    ) {
      isHydratingUrlDatesRef.current = true;
      dispatch(
        storeDateRange({
          start_date: urlStartDate,
          end_date: urlEndDate,
        })
      );
    }
  }, [
    dispatch,
    urlStartDate,
    urlEndDate,
    dateRangeValue?.start_date,
    dateRangeValue?.end_date,
  ]);

  useEffect(() => {
    if (isHydratingUrlDatesRef.current) {
      if (
        dateRangeValue?.start_date === urlStartDate &&
        dateRangeValue?.end_date === urlEndDate
      ) {
        isHydratingUrlDatesRef.current = false;
      }
      return;
    }

    const params = new URLSearchParams(searchParams);
    if (dateRangeValue?.start_date && dateRangeValue?.end_date) {
      params.set("start_date", dateRangeValue.start_date);
      params.set("end_date", dateRangeValue.end_date);
    } else {
      params.delete("start_date");
      params.delete("end_date");
    }

    const newSearch = params.toString();
    const currentSearch = searchParams.toString();
    if (newSearch !== currentSearch) {
      setSearchParams(params, { replace: true });
    }
  }, [
    dateRangeValue?.start_date,
    dateRangeValue?.end_date,
    searchParams,
    setSearchParams,
    urlStartDate,
    urlEndDate,
  ]);

  useEffect(() => {
    if (!downloadRequested || !fetchCompleted) return;

    const runExport = async () => {
      await handleExport();
      setReportDownloadLoading(false);
      setDownloadRequested(false);
    };

    runExport();
  }, [downloadRequested, fetchCompleted, tables]);

  const handleExport = async () => {
    await exportExcelReport(
      localStorage.getItem("tenant_label"),
      dateRangeValue?.start_date && dateRangeValue?.end_date
        ? `${dayjs(dateRangeValue.start_date).format("DD-MMM-YYYY")} to ${dayjs(
            dateRangeValue.end_date
          ).format("DD-MMM-YYYY")}`
        : "All Dates",
      tables,
      `${localStorage.getItem("tenant_label")} Report - ${
        dateRangeValue?.start_date && dateRangeValue?.end_date
          ? `${dayjs(dateRangeValue.start_date).format("DD-MMM-YYYY")} to ${dayjs(
              dateRangeValue.end_date
            ).format("DD-MMM-YYYY")}`
          : "All Dates"
      } - ${dayjs().format("HH:mm:ss")}`
    );
  };

  const profilesNeedingReverification = socialMediaProfiles.filter(
    (profile) => profile.is_expired || profile.about_to_expire
  );

  const expiredProfilesCount = profilesNeedingReverification.filter(
    (profile) => profile.is_expired
  ).length;
  const aboutToExpireProfilesCount = profilesNeedingReverification.filter(
    (profile) => profile.about_to_expire && !profile.is_expired
  ).length;

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__header">
        <h1 className="dashboard-page__title">
          Welcome, {localStorage.getItem("username")}!
        </h1>

        <div className="dashboard-page__actions">
          <div className="dashboard-page__filter">
            <RangePickerComponent
              value={dateRangeAntdValue}
              setValue={setDateRangeAntdValue}
              width="100%"
              additionalStyle={{
                maxWidth: "320px",
                borderRadius: "18px",
                border: "0px solid #d9d9d9",
                padding: "0 10px",
                height: "40px",
                fontSize: "14px",
                color: "#000",
                fontWeight: 500,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
              allowClear={false}
            />
          </div>

          <Button
            type="primary"
            onClick={() => {
              setReportDownloadLoading(true);
              setDownloadRequested(true);
            }}
            className="dashboard-page__button"
            style={{
              height: "40px",
              borderRadius: "500px",
              fontWeight: 500,
            }}
          >
            {reportDownloadLoading ? (
              <iconify-icon icon="codex:loader" width="24" height="24"></iconify-icon>
            ) : (
              <iconify-icon
                icon="material-symbols:download-rounded"
                width="24"
                height="24"
              ></iconify-icon>
            )}
            Report
          </Button>
        </div>
      </div>

      {profilesNeedingReverification.length > 0 && (
        <Alert
          className="dashboard-expiry-alert"
          message={
            expiredProfilesCount > 0 && aboutToExpireProfilesCount > 0
              ? `${expiredProfilesCount} social media profile${
                  expiredProfilesCount === 1 ? "" : "s"
                } expired and ${aboutToExpireProfilesCount} social media profile${
                  aboutToExpireProfilesCount === 1 ? "" : "s"
                } about to expire`
              : expiredProfilesCount > 0
              ? `${expiredProfilesCount} social media profile${
                  expiredProfilesCount === 1 ? "" : "s"
                } expired`
              : `${aboutToExpireProfilesCount} social media profile${
                  aboutToExpireProfilesCount === 1 ? "" : "s"
                } expiring soon`
          }
          type="error"
          icon={<ExclamationCircleOutlined />}
          showIcon
          closable={false}
          action={
            <Button
              type="primary"
              danger
              onClick={() => navigate("/settings?tab=social-media-profile-settings")}
              style={{
                fontWeight: 600,
                borderRadius: "6px",
                boxShadow: "0 2px 4px rgba(255, 77, 79, 0.2)",
              }}
            >
              Take Action
            </Button>
          }
          style={{
            marginBottom: "20px",
            borderRadius: "8px",
            border: "1px solid #ffccc7",
          }}
        />
      )}

      <MainCards
        ctaCount={legacyCardData.ctaCount}
        creditBalance={legacyCardData.creditBalance}
        messageCount={legacyCardData.messageCount}
      />

      <div className="dashboard-row">
        <div className="dashboard-row__item">
          <ConversationTopics data={data?.ctaTopics || []} topicCount={data?.topicCount || {}} />
        </div>
        <div className="dashboard-row__item">
          <HumanInterventionTrend data={data?.ctaCount?.data || data?.ctaTypeCount || []} />
        </div>
      </div>

      <div className="dashboard-row">
        <div className="dashboard-row__item">
          <MessageAndUserTrendChart
            messageData={data?.messageCount || []}
            userData={data?.uniqueUsersPerDay || []}
          />
        </div>
      </div>

      <UnifiedReportSections
        reportData={reportData}
        channelMsgCount={data?.channelMsgCount || []}
        channelMsgCountByRawId={data?.channelMsgCountByRawId || []}
      />
    </div>
  );
};

export default DashboardComponent;
